/**
 * File parsing utilities
 * Extract text content from various file types
 */

import type ExcelJS from 'exceljs';
import type { FileParseResult, FileParseWarning } from '@/types';
import { FILE_CONSTRAINTS, ERROR_MESSAGES, MESSAGE_LIMITS, TEXT_EXTENSIONS, IMAGE_EXTENSIONS } from './constants';

// Widened to a plain string[] for `.includes()` membership tests against
// runtime-derived extensions (the const tuple's literal element type would
// otherwise reject an arbitrary string argument).
const TEXT_EXTENSION_SET: readonly string[] = TEXT_EXTENSIONS;
const IMAGE_EXTENSION_SET: string[] = [...IMAGE_EXTENSIONS];

/**
 * Thrown when an attached file that has bytes on disk (`file.size > 0`) reads
 * back as zero characters even after a retry. This is a *failed read* — most
 * often a `File` handle whose browser-side backing was reclaimed after the tab
 * sat idle/frozen — NOT a genuinely empty document (those report `size === 0`).
 * The caller blocks the send and tells the user to re-attach or refresh,
 * rather than silently shipping an empty file block the model reads as "no
 * content" (and then replies "I'm not seeing the file").
 */
export class FileReadError extends Error {
  readonly fileName: string;
  constructor(fileName: string) {
    super(`Could not read "${fileName}" — the file read back empty.`);
    this.name = 'FileReadError';
    this.fileName = fileName;
  }
}

/**
 * Thrown when a code-split parser module (pdf.js / mammoth / exceljs) fails to
 * load — most often because a deploy replaced the hashed JS chunk while this
 * tab stayed open ("Failed to fetch dynamically imported module"). The caller
 * surfaces a "refresh to load the latest version" message rather than a raw
 * fetch error. Like FileReadError, a page refresh clears it.
 */
export class ParserModuleLoadError extends Error {
  readonly fileName: string;
  constructor(fileName: string, options?: { cause?: unknown }) {
    super(`Could not load the file parser for "${fileName}".`);
    this.name = 'ParserModuleLoadError';
    this.fileName = fileName;
    if (options && 'cause' in options) {
      (this as Error & { cause?: unknown }).cause = options.cause;
    }
  }
}

/**
 * Load a code-split parser module, mapping a dynamic-import failure to a typed
 * ParserModuleLoadError so the caller can tell "the app's parser chunk is
 * stale — refresh" apart from "the file itself is bad".
 */
export async function loadParserModule<T>(
  loader: () => Promise<T>,
  fileName: string,
): Promise<T> {
  try {
    return await loader();
  } catch (error) {
    throw new ParserModuleLoadError(fileName, { cause: error });
  }
}

/**
 * Read a file's bytes, guarding against the same stale-handle empty read the
 * text path handles: a file with bytes on disk (`size > 0`) whose ArrayBuffer
 * comes back zero-length is a failed read, not an empty file. Retry once, then
 * throw FileReadError so the caller blocks the send. A genuinely empty file
 * (`size === 0`) passes through untouched.
 */
export async function readArrayBufferGuarded(file: File): Promise<ArrayBuffer> {
  let buffer = await file.arrayBuffer();
  if (file.size > 0 && buffer.byteLength === 0) {
    buffer = await file.arrayBuffer();
    if (buffer.byteLength === 0) {
      throw new FileReadError(file.name);
    }
  }
  return buffer;
}

export interface ExtractPDFResult {
  text: string;
  /**
   * Set when extraction stopped before reading every page. `processed` is
   * how many pages we got through before crossing the char budget;
   * `total` is the PDF's full page count.
   */
  truncatedPages?: { processed: number; total: number };
}

/**
 * Cap on how much PDF text we extract. The downstream Azure OpenAI
 * context is bounded by `MESSAGE_LIMITS.MAX_CONTEXT_LENGTH` (~100 KB);
 * we extract 2× that so the prompt builder has slack when combining
 * file content with RAG context and conversation history. Anything we
 * extract beyond this would just be trimmed downstream.
 */
export const PDF_MAX_EXTRACTED_CHARS = 200_000;

export async function extractPDFText(file: File): Promise<ExtractPDFResult> {
  try {
    console.log('[PDF Parser] Starting PDF extraction for:', file.name);

    const pdfjsLib = await loadParserModule(() => import('pdfjs-dist'), file.name);

    try {
      console.log('[PDF Parser] Fetching worker file...');
      const workerResponse = await fetch('/pdf.worker.min.mjs');
      const workerBlob = await workerResponse.blob();
      const workerUrl = URL.createObjectURL(new Blob([workerBlob], { type: 'text/javascript' }));
      pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
      console.log('[PDF Parser] Worker loaded via blob URL');
    } catch (workerError) {
      console.warn('[PDF Parser] Failed to load worker via blob, falling back to direct path:', workerError);
      pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
    }

    const arrayBuffer = await readArrayBufferGuarded(file);
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;
    console.log('[PDF Parser] PDF loaded, pages:', pdf.numPages);

    const textParts: string[] = [];
    let processedPages = 0;
    let accumulatedLength = 0;
    let stoppedAtCharCap = false;

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .map((item) => ('str' in item ? item.str : ''))
        .filter((str) => str.trim().length > 0)
        .join(' ');
      if (pageText.trim().length > 0) {
        textParts.push(pageText);
        // +2 for the '\n\n' separator the join() will add between parts
        accumulatedLength += pageText.length + 2;
      }
      processedPages = i;
      if (accumulatedLength >= PDF_MAX_EXTRACTED_CHARS) {
        stoppedAtCharCap = true;
        break;
      }
    }

    const fullText = textParts.join('\n\n');
    const cleanedText = fullText
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    const text =
      cleanedText.length === 0
        ? 'Note: This PDF appears to contain no extractable text. It may be an image-based PDF that requires OCR.'
        : cleanedText;

    // Only flag truncation when we actually stopped early. A short PDF that
    // happens to push us over the cap on its last page is fully extracted —
    // no "Only N of N pages" warning needed.
    const truncatedPages =
      stoppedAtCharCap && processedPages < pdf.numPages
        ? { processed: processedPages, total: pdf.numPages }
        : undefined;

    return { text, truncatedPages };
  } catch (error) {
    // Preserve the typed stale-handle / stale-chunk failures so parseFile and
    // the UI can give the "re-attach or refresh" guidance instead of a raw
    // "Failed to extract text from PDF" wrapper.
    if (error instanceof FileReadError || error instanceof ParserModuleLoadError) {
      throw error;
    }
    console.error('[PDF Parser] Error extracting PDF text:', error);
    throw new Error(
      `Failed to extract text from PDF: ${error instanceof Error ? error.message : 'Unknown error'}`,
    );
  }
}

/**
 * Extract text from DOCX file
 * @param file - DOCX file
 * @returns Extracted text content
 */
export async function extractDOCXText(file: File): Promise<string> {
  const { default: mammoth } = await loadParserModule(
    () => import('mammoth'),
    file.name,
  );
  const arrayBuffer = await readArrayBufferGuarded(file);
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value;
}

/**
 * Extract text from XLSX file
 * @param file - XLSX file
 * @returns Extracted text content
 */
export interface ExtractXLSXResult {
  text: string;
  /**
   * Set when row accumulation stopped before reading every non-empty row
   * because the character budget was crossed. `processed` rows were sent,
   * out of `total`. Drives the "only the first N of M rows" warning so a
   * large spreadsheet export can't silently blow the model's input window.
   */
  truncatedRows?: { processed: number; total: number };
}

/**
 * Assemble sheet rows into the flat text representation, bounded to `maxChars`.
 * Stops accumulating rows once the budget is crossed but keeps COUNTING total
 * rows so the warning can report the full size. The header row comes first, so
 * a cap naturally preserves column names + an early data sample. Pure +
 * exported for unit testing without an exceljs fixture.
 */
export function assembleXlsxText(
  sheets: { name: string; rows: string[] }[],
  maxChars: number,
): ExtractXLSXResult {
  const textParts: string[] = [];
  let totalRows = 0;
  let keptRows = 0;
  let usedChars = 0;
  let truncated = false;

  for (const sheet of sheets) {
    const header = `Sheet: ${sheet.name}`;
    usedChars += header.length + 2; // header + the "\n" / "\n\n" separators
    const kept: string[] = [];
    for (const cells of sheet.rows) {
      totalRows++;
      if (truncated) continue; // keep counting total, stop accumulating
      if (usedChars + cells.length + 1 > maxChars) {
        truncated = true;
        continue;
      }
      kept.push(cells);
      usedChars += cells.length + 1;
      keptRows++;
    }
    textParts.push(`${header}\n${kept.join('\n')}`);
  }

  const text = textParts.join('\n\n');
  return truncated
    ? { text, truncatedRows: { processed: keptRows, total: totalRows } }
    : { text };
}

export async function extractXLSXText(
  file: File,
  maxChars: number = MESSAGE_LIMITS.MAX_CONTEXT_LENGTH,
): Promise<ExtractXLSXResult> {
  const ExcelJS = (await loadParserModule(() => import('exceljs'), file.name))
    .default;
  const arrayBuffer = await readArrayBufferGuarded(file);
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);

  // Bound the extracted text so a huge export (e.g. a 10k-row Power BI dump)
  // can't produce hundreds of KB of context that exceeds the model window.
  const sheets: { name: string; rows: string[] }[] = [];
  workbook.eachSheet((sheet) => {
    const rows: string[] = [];
    sheet.eachRow((row) => {
      const values = (row.values as ExcelJS.CellValue[]).slice(1);
      const cells = values.map((v) => (v != null ? String(v) : '')).join(',');
      if (cells.trim()) rows.push(cells);
    });
    sheets.push({ name: sheet.name, rows });
  });

  return assembleXlsxText(sheets, maxChars);
}

/**
 * Extract text from plain text file
 * @param file - Text file
 * @returns Extracted text content
 */
export async function extractTextFile(file: File): Promise<string> {
  return await file.text();
}

/**
 * Extract text from any supported file type
 * @param file - File to parse
 * @param limits - Optional admin-configured limits (maxFileSizeMB, allowedExtensions)
 * @returns Parse result with content, metadata, and optional warnings
 */
export async function parseFile(
  file: File,
  limits?: {
    maxFileSizeMB: number;
    allowedExtensions: string[];
    /**
     * Per-file character cap for extracted text, scaled to the agent's
     * context window by the caller. Falls back to MAX_CONTEXT_LENGTH.
     */
    maxContentChars?: number;
  },
): Promise<FileParseResult> {
  try {
    const maxContentChars =
      limits?.maxContentChars ?? MESSAGE_LIMITS.MAX_CONTEXT_LENGTH;
    const effectiveMaxBytes = limits
      ? limits.maxFileSizeMB * 1024 * 1024
      : FILE_CONSTRAINTS.MAX_FILE_SIZE;
    const effectiveMaxMB = limits
      ? limits.maxFileSizeMB
      : FILE_CONSTRAINTS.MAX_FILE_SIZE / 1024 / 1024;

    if (file.size > effectiveMaxBytes) {
      throw new Error(ERROR_MESSAGES.FILE_TOO_LARGE(effectiveMaxMB));
    }

    let content: string;
    const warnings: FileParseWarning[] = [];

    const ext = file.name.split('.').pop()?.toLowerCase();
    const isPdf = file.type === 'application/pdf' || ext === 'pdf';
    const isDocx =
      file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      ext === 'docx';
    const isXlsx =
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      ext === 'xlsx';
    const isText =
      file.type === 'text/plain' ||
      file.type === 'text/markdown' ||
      (ext != null && TEXT_EXTENSION_SET.includes(`.${ext}`));

    if (isPdf) {
      const pdfResult = await extractPDFText(file);
      content = pdfResult.text;
      if (pdfResult.truncatedPages) {
        warnings.push({
          code: 'pdf_truncated',
          message: `Only the first ${pdfResult.truncatedPages.processed} of ${pdfResult.truncatedPages.total} pages of ${file.name} were sent to the AI.`,
          meta: pdfResult.truncatedPages,
        });
      }
    } else if (isDocx) {
      content = await extractDOCXText(file);
    } else if (isXlsx) {
      const xlsxResult = await extractXLSXText(file, maxContentChars);
      content = xlsxResult.text;
      if (xlsxResult.truncatedRows) {
        warnings.push({
          code: 'xlsx_truncated',
          message: `Only the first ${xlsxResult.truncatedRows.processed} of ${xlsxResult.truncatedRows.total} rows of ${file.name} were sent to the AI.`,
          meta: xlsxResult.truncatedRows,
        });
      }
    } else if (isText) {
      content = await extractTextFile(file);
      // A file with bytes on disk (`size > 0`) that reads back as ZERO
      // characters is a failed read, not an empty document — typically a stale
      // File handle after the tab idled. Retry the read once (clears most
      // transient cases); if it's STILL empty, throw a typed error so the
      // caller blocks the send instead of silently shipping an empty block.
      if (file.size > 0 && content.length === 0) {
        content = await extractTextFile(file);
        if (content.length === 0) {
          throw new FileReadError(file.name);
        }
      }
    } else {
      throw new Error('Unsupported file type');
    }

    // Generic backstop: cap any extracted text to the context budget so a
    // single huge DOCX/TXT (or PDF page) can't blow the model window either.
    // XLSX/PDF already truncate on their own boundaries (with their own
    // warning); only add the generic notice when nothing else flagged it.
    if (content.length > maxContentChars) {
      const originalLength = content.length;
      content = content.slice(0, maxContentChars);
      if (!warnings.some((w) => w.code === 'xlsx_truncated' || w.code === 'pdf_truncated')) {
        warnings.push({
          code: 'content_truncated',
          message: `${file.name} was shortened to fit the AI context (first ${maxContentChars.toLocaleString()} of ${originalLength.toLocaleString()} characters).`,
          meta: { kept: maxContentChars, total: originalLength },
        });
      }
    }

    return {
      success: true,
      content,
      fileName: file.name,
      fileType: file.type || 'unknown',
      metadata: {
        fileSize: file.size,
        lastModified: new Date(file.lastModified),
      },
      warnings: warnings.length > 0 ? warnings : undefined,
    };
  } catch (error) {
    // Preserve the typed read-failure / stale-chunk failures so the caller can
    // distinguish them from a generic parse error (and surface the matching
    // "re-attach" / "refresh" guidance).
    if (error instanceof FileReadError || error instanceof ParserModuleLoadError) {
      throw error;
    }
    console.error('Error parsing file:', error);
    throw new Error(
      `Failed to parse file: ${error instanceof Error ? error.message : 'Unknown error'}`,
    );
  }
}

/**
 * Parse multiple files
 * @param files - Array of files
 * @param limits - Optional admin-configured limits forwarded to each parseFile call
 * @returns Array of parse results
 */
export async function parseFiles(
  files: File[],
  limits?: {
    maxFileSizeMB: number;
    allowedExtensions: string[];
    maxContentChars?: number;
  },
): Promise<FileParseResult[]> {
  return Promise.all(files.map((file) => parseFile(file, limits)));
}

/** True when the file's extension is a vision-decodable image format. */
export function isImageFile(file: File): boolean {
  const idx = file.name.lastIndexOf('.');
  const ext = idx < 0 ? '' : file.name.slice(idx).toLowerCase();
  return IMAGE_EXTENSION_SET.includes(ext);
}

/** Read an image File into a base64 data URL (data:image/...;base64,...). */
export function imageFileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () =>
      reject(reader.error ?? new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Get file type from file
 * @param file - File
 * @returns File type string
 */
export function getFileType(
  file: File
): 'pdf' | 'docx' | 'xlsx' | 'txt' | 'md' | 'image' | 'unknown' {
  const ext = file.name.split('.').pop()?.toLowerCase();

  switch (ext) {
    case 'pdf':
      return 'pdf';
    case 'docx':
      return 'docx';
    case 'xlsx':
      return 'xlsx';
    case 'md':
      return 'md';
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'webp':
    case 'gif':
      return 'image';
    default:
      // Every other text-based format (csv, json, source code, etc.) is
      // surfaced as the generic 'txt' category for UI/icon purposes.
      return ext != null && TEXT_EXTENSION_SET.includes(`.${ext}`)
        ? 'txt'
        : 'unknown';
  }
}
